import { FirebaseOptions } from '@firebase/app';
import { EnvironmentProviders, importProvidersFrom, Provider } from '@angular/core';

import { IonicModule } from '@ionic/angular';

import {
    ANALYTICS_ESSENTIAL_EVENTS,
    ConsentDialogManager,
    FIREBASE_CONFIG_IT,
    FIREBASE_IS_NATIVE_PLATFORM_IT,
    FirebaseErrorLogger,
    provideFirebaseAnalyticsService
} from '@guardfinder/analytics';

import { ObservableConsentDialogManager } from '../Presentation/Service/consent-dialog-manager.service';
import { SentryFirebaseErrorLogger } from './sentry-firebase-error-logger.service';

/**
 * Configures and provides shared analytics services and dependencies.
 *
 * @param {boolean} isNativePlatform - Specifies if the application is running on a native platform.
 * @param {FirebaseOptions} firebaseConfig - The Firebase configuration object required for analytics setup.
 * @param privacyPolicyProvider
 * @throws {Error} Throws an error if the Firebase configuration is missing.
 * @returns {Array<Provider | EnvironmentProviders>} An array of providers and environment-specific dependencies for analytics.
 */
export const provideSharedAnalytics = (
    isNativePlatform: boolean,
    firebaseConfig: FirebaseOptions,
    privacyPolicyProvider: Provider
): Array<Provider | EnvironmentProviders> => {
    if (!firebaseConfig) {
        throw new Error('Firebase configuration is required for analytics to work.');
    }

    return [
        {
            provide: FIREBASE_CONFIG_IT, // Firebase configuration like url port etc...
            useValue: firebaseConfig
        },
        {
            provide: FIREBASE_IS_NATIVE_PLATFORM_IT, // Platform type (native: ios/android)
            useValue: isNativePlatform
        },
        {
            provide: FirebaseErrorLogger, // Error logger when call to firebase is not successful
            useClass: SentryFirebaseErrorLogger
        },
        {
            provide: ConsentDialogManager, // Service to orchestrate the consent screen
            useClass: ObservableConsentDialogManager
        },
        {
            provide: ANALYTICS_ESSENTIAL_EVENTS, // Events that should fired even user rejected tracking
            useValue: ['error_occurred', 'api_error']
        },
        privacyPolicyProvider, // Privacy policy link, provided from top level because depends on the globals
        provideFirebaseAnalyticsService(), // Analytics service provider
        importProvidersFrom(IonicModule)
    ];
};
